/**
 * Nature drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const natureDrawingsPack: ToilePack = {
    slug: 'nature-drawings',
    category: 'illustration',
    name: 'Nature drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'stream', label: 'Fern stream', subject: 'a small stream running over stones between ferns, a fallen branch across it and light on the water'},
        {key: 'sunflower', label: 'Sunflower', subject: 'a sunflower head heavy with seed, its petals curling as they dry, two leaves turning down'},
        {key: 'log', label: 'Mossy log', subject: 'a mossy fallen log with a cluster of mushrooms and ivy climbing over it, leaf litter all around'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a heron', 'winter', 'a pond'],
    directionHint: 'try: a heron',
    seo: {
        title: 'Nature Drawings: Fern Stream, Sunflower, Mossy Log',
        metaDescription: 'Nine nature studies: a fern-lined stream, a sunflower gone to seed and a mossy log with mushrooms, each in three drawing media.',
        h1: 'Nature drawings: a fern stream, a sunflower, a mossy log',
        tagline: 'Three nature studies, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three finished studies of the outdoors: a stream running over stones between ferns, a sunflower with its petals curling as they dry, and a mossy fallen log with mushrooms and ivy. Each is drawn in graphite pencil, pen and ink, and charcoal, so you can see how one subject reads in each medium. Type a different plant, animal or season, such as a heron or winter, and the same media carry it.',
        useCases: [
            'a nature study for a school sketchbook',
            'a botany class reference',
            'a framed print for a kitchen or study',
            'a copy-along drawing exercise',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three subjects: a fern-lined stream, a sunflower going to seed, and a mossy log with mushrooms. Each subject is drawn in pencil, pen and ink, and charcoal.'},
            {q: 'Can I ask for another nature subject?', a: 'Yes. Type a subject or a season, such as a heron or winter. A typed direction is a request, and the drawing may differ from the samples shown here.'},
            {q: 'How do the three styles differ?', a: 'Pencil uses graphite from sharp detail to deep darks on white paper. Pen and ink uses confident contour lines with crosshatching for tone. Charcoal uses smudged masses with crisp accents.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The nature drawings are taller than they are wide.'},
        ],
    },
}
